import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import api, { executionPlanApi } from '../services/api';
import AppUpload from '../components/AppUpload';
import TimeAgo from '../components/TimeAgo';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import { Bell, Hash, ShieldAlert, CheckCircle, Info, ChevronLeft } from 'lucide-react';
import './AppDetail.css';

const AppDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();

    const [app, setApp] = useState(null);
    const [builds, setBuilds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('builds');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [appContext, setAppContext] = useState([]);
    const [interventionEnabled, setInterventionEnabled] = useState(false);
    const [isSavingIntervention, setIsSavingIntervention] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [observabilityLinks, setObservabilityLinks] = useState(null);
    const { dialogState, closeDialog, confirm, alert } = useDialog();

    useEffect(() => {
        if (currentWorkspace && id) {
            fetchAppDetails();
            fetchBuilds();
            fetchContext();
            fetchObservabilityLinks();
        }
    }, [currentWorkspace, id]);

    const fetchAppDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/apps/${id}`);
            setApp(response.data);
            setInterventionEnabled(response.data.enableHumanIntervention || false);
            setError(null);
        } catch (err) {
            setError('Failed to load app details');
            console.error('Error fetching app:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchBuilds = async () => {
        try {
            const response = await api.get(`/apps/${id}/builds`);
            setBuilds(response.data);
        } catch (err) {
            console.error('Error fetching builds:', err);
        }
    };

    const fetchContext = async () => {
        try {
            const response = await api.get(`/apps/${id}/context`);
            setAppContext(response.data.context || []);
        } catch (err) {
            console.error('Error fetching app context:', err);
        }
    };

    const fetchObservabilityLinks = async () => {
        try {
            const response = await api.get('/integrations/observability/status', {
                params: { workspaceId: currentWorkspace.id }
            });
            if (response.data.connected && response.data.settings) {
                const s = response.data.settings;
                const resolve = (tpl) => {
                    if (!tpl) return null;
                    return tpl.replace(/{appId}/g, id).replace(/{projectId}/g, id);
                };
                setObservabilityLinks({
                    grafana: resolve(s.grafanaUrl),
                    kibana: resolve(s.kibanaUrl),
                    custom: resolve(s.customUrl),
                    customLabel: s.customLabel || 'Custom'
                });
            } else {
                setObservabilityLinks(null);
            }
        } catch (err) {
            console.error('Failed to fetch observability links:', err);
            setObservabilityLinks(null);
        }
    };

    const saveContext = async (entries) => {
        const response = await api.put(`/apps/${id}/context`, { context: entries });
        setAppContext(response.data.context || []);
    };

    const handleUploadSuccess = async () => {
        // Refresh builds list
        await fetchBuilds();
        await fetchAppDetails();
        setShowUploadModal(false);

        await alert({
            title: 'Success',
            message: 'Build uploaded successfully!',
            variant: 'success'
        });
    };

    const handleDeleteApp = async () => {
        const confirmed = await confirm({
            title: 'Delete App',
            message: `Are you sure you want to delete "${app.displayName || app.name}"? This will also delete all builds and test history. This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await api.delete(`/apps/${id}`);
            navigate('/apps');
        } catch (err) {
            console.error('Error deleting app:', err);
            await alert({
                title: 'Error',
                message: 'Failed to delete app. Please try again.',
                variant: 'danger'
            });
        }
    };

    const handleSaveIntervention = async () => {
        setIsSavingIntervention(true);
        try {
            await api.put(`/apps/${id}`, { enableHumanIntervention: interventionEnabled });
            setApp(prev => ({ ...prev, enableHumanIntervention: interventionEnabled }));
            await alert({
                title: 'Success',
                message: 'Advanced capabilities updated successfully',
                variant: 'success'
            });
        } catch (err) {
            console.error('Failed to update setting:', err);
            await alert({
                title: 'Error',
                message: 'Failed to update settings. Please try again.',
                variant: 'danger'
            });
        } finally {
            setIsSavingIntervention(false);
        }
    };

    const getHealthIcon = (status) => {
        const icons = {
            healthy: '🟢',
            flaky: '🟡',
            failing: '🔴'
        };
        return icons[status] || '⚪';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return formatDate(dateString);
    };

    if (loading) {
        return (
            <div className="app-detail-container">
                <div className="loading">Loading app details...</div>
            </div>
        );
    }

    if (error || !app) {
        return (
            <div className="app-detail-container">
                <div className="error-message">{error || 'App not found'}</div>
                <button onClick={() => navigate('/apps')} className="btn-secondary">
                    ← Back to Apps
                </button>
            </div>
        );
    }

    return (
        <div className="app-detail-container">
            {/* Header */}
            <div className="app-detail-header">
                <button onClick={() => navigate('/apps')} className="back-button">
                    ← Back
                </button>

                <div className="header-content">
                    <div className="header-left">
                        <div className="app-title-row">
                            <h2>{app.displayName || app.name}</h2>

                            {app.healthStatus && (
                                <div className={`health-badge-large ${app.healthStatus}`}>
                                    {getHealthIcon(app.healthStatus)}
                                    <span className="health-label">{app.healthStatus}</span>
                                </div>
                            )}

                        </div>

                    </div>

                    {/* <div className="header-stats">
                        <div className="stat-card">
                            <div className="stat-value">{app.testCount || 0}</div>
                            <div className="stat-label">Total Tests</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">
                                {app.testCount > 0
                                    ? Math.round((app.passCount / app.testCount) * 100)
                                    : 0}%
                            </div>
                            <div className="stat-label">Pass Rate</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">
                                {app.lastTestedAt ? <TimeAgo date={app.lastTestedAt} /> : 'Never'}
                            </div>
                            <div className="stat-label">Last Tested</div>
                        </div>
                    </div> */}
                </div>
                <p className="app-package-name">{app.packageName}</p>
                <div className="app-metadata">
                    {app.uploader && (
                        <span className="metadata-item">
                            Uploaded By: {app.uploader.fullName || app.uploader.firstName || app.uploader.email}
                        </span>
                    )}
                    <span className="metadata-item">
                        Size: {formatFileSize(app.fileSize)}
                    </span>
                    <span className="metadata-item">
                        Uploaded: <TimeAgo date={app.createdAt} />
                    </span>

                {observabilityLinks && (observabilityLinks.grafana || observabilityLinks.kibana || observabilityLinks.custom) && (
                    <div className="observability-dashboards-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', padding: '12px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            📊 Observability Dashboards:
                        </span>
                        {observabilityLinks.grafana && (
                            <a href={observabilityLinks.grafana} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 12px', textDecoration: 'none' }}>
                                Grafana
                            </a>
                        )}
                        {observabilityLinks.kibana && (
                            <a href={observabilityLinks.kibana} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 12px', textDecoration: 'none' }}>
                                Kibana
                            </a>
                        )}
                        {observabilityLinks.custom && (
                            <a href={observabilityLinks.custom} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 12px', textDecoration: 'none' }}>
                                {observabilityLinks.customLabel}
                            </a>
                        )}
                    </div>
                )}
                    <button
                        className="btn-sm btn btn-secondary"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => setShowEditModal(true)}
                    >
                        Edit Details
                    </button>
                    <button
                        className="btn-sm btn btn-primary"
                        onClick={async () => {
                            try {
                                const response = await api.get(`/apps/${app.id}/download`);
                                window.open(response.data.downloadUrl, '_blank');
                            } catch (err) {
                                await alert({
                                    title: 'Error',
                                    message: 'Failed to generate download link. Please try again.',
                                    variant: 'danger'
                                });
                            }
                        }}
                    >
                        Download
                    </button>
                    <button
                        className="btn-sm btn btn-danger"
                        onClick={handleDeleteApp}
                    >
                        Delete App
                    </button>
                </div>

                {!app.packageName && (
                    <div className="warning-banner" style={{ marginTop: '15px', padding: '10px', backgroundColor: 'rgba(255, 152, 0, 0.1)', border: '1px solid #ff9800', borderRadius: '4px', color: '#ff9800', fontSize: '13px' }}>
                        ⚠️ <strong>Missing Package Name:</strong> This app cannot be automatically activated on devices. Please click "Edit Details" to provide a Package Name / Bundle ID.
                    </div>
                )}

                {/* Advanced Settings */}
                <div className="app-settings-section" style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h3 style={{ fontSize: '14px', margin: 0 }}>Advanced Capabilities</h3>
                        {interventionEnabled !== (app?.enableHumanIntervention || false) && (
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSaveIntervention}
                                disabled={isSavingIntervention}
                            >
                                {isSavingIntervention ? 'Saving...' : 'Save Settings'}
                            </button>
                        )}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', width: 'fit-content' }}>
                        <input
                            type="checkbox"
                            checked={interventionEnabled}
                            onChange={(e) => setInterventionEnabled(e.target.checked)}
                        />
                        <span style={{ fontWeight: '500' }}>Enable Human Intervention</span>
                    </label>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0 22px', maxWidth: '600px', lineHeight: '1.4' }}>
                        When enabled, Rova will pause the test and ask for your help if it encounters genuine blockers like CAPTCHAs, Two-Factor Authentication (2FA), or system-level actions it cannot perform. If disabled, Rova will simply fail the test when blocked.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs-container">
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'builds' ? 'active' : ''}`}
                        onClick={() => setActiveTab('builds')}
                    >
                        📦 Builds ({builds.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'context' ? 'active' : ''}`}
                        onClick={() => setActiveTab('context')}
                    >
                        🧠 Test Context {appContext.length > 0 ? `(${appContext.length})` : ''}
                    </button>
                    {/* <button
                        className={`tab ${activeTab === 'integrations' ? 'active' : ''}`}
                        onClick={() => setActiveTab('integrations')}
                    >
                        🔌 Integrations
                    </button> */}
                </div>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'builds' && (
                    <BuildsTab
                        appId={id}
                        appPlatform={app.platform}
                        builds={builds}
                        latestBuildId={app.latestBuildId}
                        onUpload={() => setShowUploadModal(true)}
                        onRefresh={fetchBuilds}
                    />
                )}
                {activeTab === 'context' && (
                    <ContextTab
                        context={appContext}
                        onSave={saveContext}
                    />
                )}
                {activeTab === 'integrations' && (
                    <IntegrationsTab
                        app={app}
                        alert={alert}
                        confirm={confirm}
                        onSave={async (config) => {
                            try {
                                const response = await api.put(`/apps/${id}`, { metadata: { repositoryConfig: config } });
                                setApp(response.data);
                                await alert({ title: 'Success', message: 'Integration settings saved successfully!', variant: 'success' });
                            } catch (err) {
                                await alert({ title: 'Error', message: 'Failed to save settings', variant: 'danger' });
                            }
                        }}
                    />
                )}
            </div>

            {/* Upload Build Modal */}
            {showUploadModal && (
                <AppUpload
                    onSuccess={handleUploadSuccess}
                    onClose={() => setShowUploadModal(false)}
                    parentAppId={id}
                />
            )}

            {/* Edit App Details Modal */}
            {showEditModal && (
                <EditAppModal
                    app={app}
                    onSuccess={async () => {
                        await fetchAppDetails();
                        setShowEditModal(false);
                    }}
                    onClose={() => setShowEditModal(false)}
                />
            )}

            <Dialog {...dialogState} onClose={closeDialog} />
        </div>
    );
};

// Builds Tab Component
const BuildsTab = ({ appId, appPlatform, builds, latestBuildId, onUpload, onRefresh }) => {
    return (
        <div className="builds-tab">
            <div className="tab-header">
                <h2>Build History</h2>
                <button onClick={onUpload} className="btn btn-primary">
                    + Upload New Build
                </button>
            </div>

            {builds.length === 0 ? (
                <div className="empty-state">
                    <p>No builds yet. Upload your first build to get started.</p>
                </div>
            ) : (
                <div className="builds-list">
                    {builds.map((build) => (
                        <BuildItem
                            key={build.id}
                            build={build}
                            appId={appId}
                            platform={appPlatform}
                            isLatest={build.id === latestBuildId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Build Item Component
const BuildItem = ({ build, appId, platform, isLatest }) => {
    const navigate = useNavigate();
    const [launching, setLaunching] = useState(false);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleStartLiveSession = async () => {
        setLaunching(true);
        try {
            const response = await api.post('/interactive-sessions/start', {
                appId,
                platform,
                idleTimeoutMinutes: 10
            });
            navigate(`/interactive-session/${response.data.sessionId}`);
        } catch (err) {
            console.error('Failed to start interactive session:', err);
            alert('Failed to start interactive session. Please try again.');
        } finally {
            setLaunching(false);
        }
    };

    return (
        <div className="build-item">
            <div className="build-info">
                <div className="build-version-info">
                    <h3>v{build.version}</h3>
                    {build.buildNumber && <span className="build-number">Build {build.buildNumber}</span>}
                    {isLatest && <span className="latest-badge">Latest</span>}
                </div>
                <div className="build-meta">
                    <span>{formatFileSize(build.fileSize)}</span>
                    <span>•</span>
                    <span>{formatDate(build.createdAt)}</span>
                    {build.uploader && (
                        <>
                            <span>•</span>
                            <span>by {build.uploader.fullName || build.uploader.firstName || build.uploader.email}</span>
                        </>
                    )}
                </div>
            </div>

            <div className="build-actions" style={{ display: 'flex', gap: '8px' }}>
                <button
                    className="btn-primary btn-sm btn"
                    onClick={handleStartLiveSession}
                    disabled={launching}
                >
                    {launching ? 'Launching...' : '⚡ Live Session'}
                </button>
                <button className="btn-secondary btn-sm btn">Download</button>
                {/* {!isLatest && <button className="btn-secondary btn-sm btn">Set as Latest</button>} */}
            </div>
        </div>
    );
};

// Test Goals Tab Component
const TestGoalsTab = ({ appId }) => {
    return (
        <div className="test-goals-tab">
            <div className="tab-header">
                <h2>Test Goals</h2>
                <button className="btn-primary">+ Create Test Goal</button>
            </div>
            <div className="empty-state">
                <p>Test goals feature coming soon!</p>
            </div>
        </div>
    );
};

// Test History Tab Component
const TestHistoryTab = ({ appId }) => {
    return (
        <div className="test-history-tab">
            <div className="tab-header">
                <h2>Test Execution History</h2>
            </div>
            <div className="empty-state">
                <p>Test history feature coming soon!</p>
            </div>
        </div>
    );
};

// Trends Tab Component
const TrendsTab = ({ appId }) => {
    return (
        <div className="trends-tab">
            <div className="tab-header">
                <h2>Trends & Analytics</h2>
            </div>
            <div className="empty-state">
                <p>Trends visualization coming soon!</p>
                <p className="hint">Will show pass rate over time, duration trends, and more.</p>
            </div>
        </div>
    );
};

// Integrations Tab Component
const IntegrationsTab = ({ app, onSave, alert, confirm }) => {
    const initialConfig = app.metadata?.repositoryConfig || {};

    const [config, setConfig] = useState({
        slackChannel: initialConfig.slackChannel || '',
        notifyOnPlanStart: initialConfig.notifyOnPlanStart !== undefined ? initialConfig.notifyOnPlanStart : false,
        notifyOnPlanCompletion: initialConfig.notifyOnPlanCompletion !== undefined ? initialConfig.notifyOnPlanCompletion : true,
        notifyOnPlanFailure: initialConfig.notifyOnPlanFailure !== undefined ? initialConfig.notifyOnPlanFailure : true,
        notifyOnGoalSuccess: initialConfig.notifyOnGoalSuccess !== undefined ? initialConfig.notifyOnGoalSuccess : false,
        notifyOnGoalFailure: initialConfig.notifyOnGoalFailure !== undefined ? initialConfig.notifyOnGoalFailure : true,
        linearProjectId: initialConfig.linearProjectId || '',
        autoBug: initialConfig.autoBug || false,
        regressionPlanId: initialConfig.regressionPlanId || '',
        regressionPlanName: initialConfig.regressionPlanName || '',
    });

    const [slackChannels, setSlackChannels] = useState([]);
    const [slackLoading, setSlackLoading] = useState(false);
    const [isTestingSlack, setIsTestingSlack] = useState(false);

    // Fetch Slack channels
    useEffect(() => {
        const fetchChannels = async () => {
            setSlackLoading(true);
            try {
                const { data } = await api.get('/integrations/slack/channels');
                setSlackChannels(data.channels || []);
            } catch (err) {
                console.error('Failed to fetch Slack channels:', err);
                setSlackChannels([]);
            } finally {
                setSlackLoading(false);
            }
        };

        fetchChannels();
    }, []);

    // ── Regression plan search state ──────────────────────────────────────────
    const [planQuery, setPlanQuery] = useState('');
    const [planResults, setPlanResults] = useState([]);
    const [planLoading, setPlanLoading] = useState(false);
    const [planFocused, setPlanFocused] = useState(false);
    const [selectedPlanName, setSelectedPlanName] = useState(initialConfig.regressionPlanName || '');
    const planDebounceRef = React.useRef(null);
    const planDropdownRef = React.useRef(null);

    // Close dropdown on outside click
    React.useEffect(() => {
        const handleOutside = (e) => {
            if (planDropdownRef.current && !planDropdownRef.current.contains(e.target)) {
                setPlanFocused(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, []);

    const searchPlans = (q) => {
        clearTimeout(planDebounceRef.current);
        planDebounceRef.current = setTimeout(async () => {
            if (!q.trim()) { setPlanResults([]); return; }
            setPlanLoading(true);
            try {
                const { data } = await executionPlanApi.getExecutionPlans({ search: q, limit: 8 });
                setPlanResults(data.executionPlans || data.plans || []);
            } catch {
                setPlanResults([]);
            } finally {
                setPlanLoading(false);
            }
        }, 300);
    };

    const selectPlan = (plan) => {
        setPlanFocused(false);
        setConfig(prev => ({
            ...prev,
            regressionPlanId: plan.id,
            regressionPlanName: plan.name
        }));
        setSelectedPlanName(plan.name);
        setPlanQuery('');
        setPlanResults([]);
        setPlanFocused(false);
    };

    const clearPlan = () => {
        setConfig(prev => ({
            ...prev,
            regressionPlanId: '',
            regressionPlanName: ''
        }));
        setSelectedPlanName('');
        setPlanQuery('');
        setPlanResults([]);
    };
    // ─────────────────────────────────────────────────────────────────────────

    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        await onSave(config);
        setIsSaving(false);
    };

    return (
        <div className="integrations-tab" style={{ padding: '20px', maxWidth: '800px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h2 style={{ marginBottom: '20px' }}>Repository Integrations Configuration</h2>
            <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>Configure how Rova interacts with this app's linked git repository and connected tools. Triggering logic is now managed via your CI workflow.</p>

            <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Slack Notification Channel</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <select
                        name="slackChannel"
                        value={config.slackChannel}
                        onChange={handleChange}
                        className="form-control"
                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                        disabled={slackLoading}
                    >
                        <option value="">Select a channel...</option>
                        {slackChannels.map(ch => (
                            <option key={ch.id} value={ch.id}>#{ch.name} {ch.is_private ? '🔒' : ''}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={async () => {
                            if (!config.slackChannel) {
                                await alert({ title: 'Warning', message: 'Please select a Slack channel first.', variant: 'warning' });
                                return;
                            }
                            setIsTestingSlack(true);
                            try {
                                const response = await api.post('/integrations/slack/test', { channelId: config.slackChannel });
                                await alert({ title: 'Success', message: response.data.message, variant: 'success' });
                            } catch (err) {
                                await alert({
                                    title: 'Error',
                                    message: err.response?.data?.error || 'Failed to send test message. Make sure Slack is connected.',
                                    variant: 'danger'
                                });
                            } finally {
                                setIsTestingSlack(false);
                            }
                        }}
                        disabled={isTestingSlack || !config.slackChannel || slackLoading}
                        className="btn btn-secondary"
                        style={{ whiteSpace: 'nowrap', padding: '8px 16px' }}
                    >
                        {isTestingSlack ? 'Testing...' : 'Test Connection'}
                    </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
                    Only public channels are fetched by default. If you want to use a private channel, please invite the Rova bot to that channel by using the <code>/invite @Rova</code> command in Slack.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    <div className="toggle-item">
                        <div className="toggle-info">
                            <Bell size={18} className="info-icon" />
                            <div>
                                <span className="toggle-label">Plan Start</span>
                                <span className="toggle-desc">Announce when a test run begins.</span>
                            </div>
                        </div>
                        <label className="switch">
                            <input type="checkbox" name="notifyOnPlanStart" checked={config.notifyOnPlanStart} onChange={handleChange} />
                            <span className="slider round"></span>
                        </label>
                    </div>

                    <div className="toggle-item">
                        <div className="toggle-info">
                            <CheckCircle size={18} className="success-icon" />
                            <div>
                                <span className="toggle-label">Plan Completion</span>
                                <span className="toggle-desc">Summary when a plan finishes.</span>
                            </div>
                        </div>
                        <label className="switch">
                            <input type="checkbox" name="notifyOnPlanCompletion" checked={config.notifyOnPlanCompletion} onChange={handleChange} />
                            <span className="slider round"></span>
                        </label>
                    </div>

                    <div className="toggle-item">
                        <div className="toggle-info">
                            <ShieldAlert size={18} className="failure-icon" />
                            <div>
                                <span className="toggle-label">Plan Failures</span>
                                <span className="toggle-desc">Alert when entire plan fails.</span>
                            </div>
                        </div>
                        <label className="switch">
                            <input type="checkbox" name="notifyOnPlanFailure" checked={config.notifyOnPlanFailure} onChange={handleChange} />
                            <span className="slider round"></span>
                        </label>
                    </div>

                    <div className="toggle-item">
                        <div className="toggle-info">
                            <CheckCircle size={18} className="goal-success-icon" />
                            <div>
                                <span className="toggle-label">Goal Success</span>
                                <span className="toggle-desc">Thread reply for each passing goal.</span>
                            </div>
                        </div>
                        <label className="switch">
                            <input type="checkbox" name="notifyOnGoalSuccess" checked={config.notifyOnGoalSuccess} onChange={handleChange} />
                            <span className="slider round"></span>
                        </label>
                    </div>

                    <div className="toggle-item">
                        <div className="toggle-info">
                            <ShieldAlert size={18} className="failure-icon" />
                            <div>
                                <span className="toggle-label">Goal Failure</span>
                                <span className="toggle-desc">Alert when individual goal fails.</span>
                            </div>
                        </div>
                        <label className="switch">
                            <input type="checkbox" name="notifyOnGoalFailure" checked={config.notifyOnGoalFailure} onChange={handleChange} />
                            <span className="slider round"></span>
                        </label>
                    </div>
                </div>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Linear / Jira Project ID</label>
                <input
                    type="text"
                    name="linearProjectId"
                    value={config.linearProjectId}
                    onChange={handleChange}
                    placeholder="e.g., PRJ-123"
                    className="form-control"
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                />
            </div>

            <div className="" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                    <input
                        type="checkbox"
                        name="autoBug"
                        checked={config.autoBug}
                        onChange={handleChange}
                    />
                    Auto-create bug tickets in Task Manager for failed PR goals
                </label>
            </div>

            <div className="form-group" style={{ marginBottom: '20px', position: 'relative' }} ref={planDropdownRef}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Regression Run (Execution Plan)</label>

                {/* Show selected plan chip, or the search input */}
                {selectedPlanName && !planFocused ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
                        <span style={{ flex: 1, fontSize: '0.875rem' }}>📋 {selectedPlanName}</span>
                        <button
                            type="button"
                            onClick={clearPlan}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '2px 4px', borderRadius: '4px' }}
                            title="Clear selection"
                        >✕</button>
                    </div>
                ) : (
                    <input
                        type="text"
                        value={planQuery}
                        onChange={e => { setPlanQuery(e.target.value); searchPlans(e.target.value); }}
                        onFocus={() => setPlanFocused(true)}
                        placeholder={selectedPlanName || 'Search execution plans…'}
                        className="form-control"
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                    />
                )}

                {/* Dropdown results */}
                {planFocused && (planResults.length > 0 || planLoading) && (
                    <div style={{
                        position: 'absolute', zIndex: 100, top: '100%', left: 0, right: 0,
                        backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                        borderRadius: '6px', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        overflow: 'hidden', maxHeight: '220px', overflowY: 'auto'
                    }}>
                        {planLoading && (
                            <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Searching…</div>
                        )}
                        {planResults.map(plan => (
                            <div
                                key={plan.id}
                                onMouseDown={() => selectPlan(plan)}
                                style={{
                                    padding: '9px 14px', cursor: 'pointer', fontSize: '0.875rem',
                                    display: 'flex', flexDirection: 'column', gap: '2px',
                                    borderBottom: '1px solid var(--border-color)'
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                            >
                                <span style={{ fontWeight: 500 }}>{plan.name}</span>
                                {plan.description && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{plan.description}</span>}
                            </div>
                        ))}
                        {!planLoading && planResults.length === 0 && planQuery && (
                            <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>No plans found for "{planQuery}"</div>
                        )}
                    </div>
                )}

                {config.regressionPlanId && (
                    <p style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>ID: {config.regressionPlanId}</p>
                )}
            </div>

            <button
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-primary"
            >
                {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
        </div>
    );
};

// ── Context Tab: guided template card UX ──────────────────────────────────────

const CONTEXT_TEMPLATES = [
    {
        id: 'credentials',
        icon: '🔐',
        label: 'Login Credentials',
        desc: 'Test account email & password',
        color: 'purple',
        fields: [
            { id: 'email', label: 'Email / Username', placeholder: 'e.g. tester@myapp.com', type: 'email' },
            { id: 'password', label: 'Password', placeholder: 'e.g. Test1234!', type: 'password' },
        ],
    },
    {
        id: 'environment',
        icon: '🌐',
        label: 'Environment Note',
        desc: 'Staging info, feature flags, test data',
        color: 'blue',
        fields: [
            { id: 'note', label: 'Note', placeholder: 'e.g. OTP code is always 123456 in staging', type: 'text' },
        ],
    },
    {
        id: 'hint',
        icon: '💡',
        label: 'Additional Info',
        desc: 'Any other relevant context',
        color: 'amber',
        fields: [
            { id: 'hint', label: 'Hint', placeholder: 'e.g. Checkout button only appears after selecting a delivery address', type: 'text' },
        ],
    },
];

const TEMPLATE_MAP = Object.fromEntries(CONTEXT_TEMPLATES.map(t => [t.id, t]));

// Infer a display type from an entry key so legacy entries still get icons
function inferType(key) {
    const k = (key || '').toLowerCase();
    if (k.includes('email') || k.includes('username') || k.includes('password') || k.includes('login') || k.includes('pin') || k.includes('credential')) return 'credentials';
    if (k.includes('hint') || k.includes('navigation') || k.includes('checkout') || k.includes('step')) return 'hint';
    return 'environment';
}

const ContextTab = ({ context, onSave }) => {
    const [entries, setEntries] = useState(context || []);
    const [activeTemplate, setActive] = useState(null); // template id or null
    const [draft, setDraft] = useState({});    // field values for the open form
    const [draftErrors, setDraftErrors] = useState({});
    const [editingIndex, setEditingIndex] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);  // 'saved' | 'error'

    // Sync when parent loads fresh data
    useEffect(() => { setEntries(context || []); }, [context]);

    // Check if entries have changed from initial context
    const isDirty = JSON.stringify(entries) !== JSON.stringify(context || []);

    // Open / toggle a template card
    const openTemplate = (id) => {
        if (activeTemplate === id) { setActive(null); setDraft({}); setDraftErrors({}); return; }
        setActive(id);
        setDraft({});
        setDraftErrors({});
    };

    // Commit the inline form as new entries
    const commitDraft = () => {
        const tpl = TEMPLATE_MAP[activeTemplate];
        const errors = {};
        tpl.fields.forEach(f => { if (!draft[f.id]?.trim()) errors[f.id] = 'Required'; });
        if (Object.keys(errors).length) { setDraftErrors(errors); return; }

        let newEntries;
        if (activeTemplate === 'credentials') {
            newEntries = [
                { key: 'Test email', value: draft.email.trim(), type: 'credentials' },
                { key: 'Test password', value: draft.password.trim(), type: 'credentials' },
            ];
        } else if (activeTemplate === 'environment') {
            newEntries = [{ key: 'Environment note', value: draft.note.trim(), type: 'environment' }];
        } else {
            newEntries = [{ key: 'Additional info', value: draft.hint.trim(), type: 'hint' }];
        }
        setEntries(prev => [...prev, ...newEntries]);
        setActive(null);
        setDraft({});
        setDraftErrors({});
    };

    const removeEntry = (index) => setEntries(prev => prev.filter((_, i) => i !== index));

    const startEditing = (index, value) => {
        setEditingIndex(index);
        setEditValue(value);
    };

    const cancelEditing = () => {
        setEditingIndex(null);
        setEditValue('');
    };

    const saveEdit = (index) => {
        if (!editValue.trim()) return;
        setEntries(prev => prev.map((entry, i) =>
            i === index ? { ...entry, value: editValue.trim() } : entry
        ));
        setEditingIndex(null);
        setEditValue('');
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveStatus(null);
        try {
            await onSave(entries);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(null), 2500);
        } catch (err) {
            console.error('Failed to save context:', err);
            setSaveStatus('error');
        } finally {
            setSaving(false);
        }
    };

    const tpl = activeTemplate ? TEMPLATE_MAP[activeTemplate] : null;
    const hasEntries = entries.length > 0;

    return (
        <div className="context-tab">

            {/* ── Header ── */}
            <div className="context-tab-topbar">
                <div>
                    <h2 className="context-tab-heading">Test Context</h2>
                    <p className="context-tab-desc">Tell Rova what it should always know before testing this app.</p>
                </div>
                <div className="context-tab-actions">
                    {saveStatus === 'saved' && <span className="ctx-save-status ctx-save-status--ok">✓ Saved</span>}
                    {saveStatus === 'error' && <span className="ctx-save-status ctx-save-status--err">✗ Save failed</span>}
                    {isDirty && (
                        <button onClick={handleSave} className="btn btn-primary btn-sm" disabled={saving}>
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    )}
                </div>
            </div>

            {/* ── Template cards ── */}
            <div className="ctx-section-label">What does Rova need to know?</div>
            <div className="ctx-template-cards">
                {CONTEXT_TEMPLATES.map(t => (
                    <button
                        key={t.id}
                        className={`ctx-card ctx-card--${t.color} ${activeTemplate === t.id ? 'ctx-card--active' : ''}`}
                        onClick={() => openTemplate(t.id)}
                    >
                        <span className="ctx-card-icon">{t.icon}</span>
                        <span className="ctx-card-label">{t.label}</span>
                        <span className="ctx-card-desc">{t.desc}</span>
                        <span className="ctx-card-chevron">{activeTemplate === t.id ? '▲' : '＋'}</span>
                    </button>
                ))}
            </div>

            {/* ── Inline form ── */}
            {tpl && (
                <div className={`ctx-inline-form ctx-inline-form--${tpl.color}`}>
                    <div className="ctx-inline-form-header">
                        <span>{tpl.icon} {tpl.label}</span>
                    </div>
                    <div className="ctx-inline-form-body">
                        {tpl.fields.map(f => (
                            <div key={f.id} className="ctx-field">
                                <label className="ctx-field-label">{f.label}</label>
                                <input
                                    className={`ctx-field-input ${draftErrors[f.id] ? 'ctx-field-input--err' : ''}`}
                                    type={f.type === 'password' ? 'text' : f.type}
                                    placeholder={f.placeholder}
                                    value={draft[f.id] || ''}
                                    onChange={e => { setDraft(d => ({ ...d, [f.id]: e.target.value })); setDraftErrors(d => ({ ...d, [f.id]: '' })); }}
                                    onKeyDown={e => e.key === 'Enter' && commitDraft()}
                                    autoFocus={f.id === tpl.fields[0].id}
                                />
                                {draftErrors[f.id] && <span className="ctx-field-error">{draftErrors[f.id]}</span>}
                            </div>
                        ))}
                    </div>
                    <div className="ctx-inline-form-footer">
                        <button className="btn btn-secondary btn-sm" onClick={() => { setActive(null); setDraft({}); setDraftErrors({}); }}>Cancel</button>
                        <button className="btn btn-primary btn-sm" onClick={commitDraft}>Add</button>
                    </div>
                </div>
            )}

            {/* ── Entry list ── */}
            {hasEntries && (
                <div className="ctx-entries">
                    <div className="ctx-section-label">What Rova knows ({entries.length} {entries.length === 1 ? 'item' : 'items'})</div>
                    {entries.map((entry, i) => {
                        const entryType = entry.type || inferType(entry.key);
                        const entryTpl = TEMPLATE_MAP[entryType] || CONTEXT_TEMPLATES[1];
                        return (
                            <div key={i} className={`ctx-entry ctx-entry--${entryTpl.color}`}>
                                <span className="ctx-entry-icon">{entryTpl.icon}</span>
                                <div className="ctx-entry-body">
                                    <span className="ctx-entry-key">{entry.key}</span>
                                    {editingIndex === i ? (
                                        <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                                            <input
                                                className="ctx-field-input"
                                                style={{ padding: '4px 8px', fontSize: '13px' }}
                                                value={editValue}
                                                onChange={e => setEditValue(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') saveEdit(i);
                                                    if (e.key === 'Escape') cancelEditing();
                                                }}
                                                autoFocus
                                            />
                                            <button className="btn btn-primary btn-sm" onClick={() => saveEdit(i)}>Save</button>
                                            <button className="btn btn-secondary btn-sm" onClick={cancelEditing}>Cancel</button>
                                        </div>
                                    ) : (
                                        <span className="ctx-entry-value">{entry.value}</span>
                                    )}
                                </div>
                                {editingIndex !== i && (
                                    <div className="ctx-entry-actions" style={{ display: 'flex', gap: '4px' }}>
                                        <button className="ctx-entry-remove" onClick={() => startEditing(i, entry.value)} title="Edit">✎</button>
                                        <button className="ctx-entry-remove" onClick={() => removeEntry(i)} title="Remove">✕</button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div className="ctx-save-row">
                        {saveStatus === 'saved' && <span className="ctx-save-status ctx-save-status--ok">✓ Saved</span>}
                        {saveStatus === 'error' && <span className="ctx-save-status ctx-save-status--err">✗ Save failed</span>}
                        <button onClick={handleSave} className="btn btn-primary btn-sm" disabled={saving}>
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── First-time zero state (no entries, no card open) ── */}
            {!hasEntries && !activeTemplate && (
                <div className="ctx-zero-state">
                    <p>Click a card above to tell Rova about your app.</p>
                </div>
            )}
        </div>
    );
};

// Export
// Edit App Details Modal Component
const EditAppModal = ({ app, onSuccess, onClose }) => {
    const [formData, setFormData] = useState({
        displayName: app.displayName || '',
        packageName: app.packageName || '',
        version: app.version || '',
        buildNumber: app.buildNumber || '',
        launcherActivity: app.launcherActivity || ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);

        try {
            await api.put(`/apps/${app.id}`, formData);
            onSuccess();
        } catch (err) {
            console.error('Failed to update app:', err);
            setError(err.response?.data?.error || 'Failed to update app details. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h2>Edit App Details</h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="edit-app-form">
                    {error && <div className="error-banner" style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}

                    <div className="form-group" style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Display Name</label>
                        <input
                            type="text"
                            name="displayName"
                            value={formData.displayName}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>

                    <div className="form-group" style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Package Name / Bundle ID</label>
                        <input
                            type="text"
                            name="packageName"
                            value={formData.packageName}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="e.g. com.company.app"
                        />
                        <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>Required for automatic app activation</p>
                    </div>

                    <div className="form-row" style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Version</label>
                            <input
                                type="text"
                                name="version"
                                value={formData.version}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="e.g. 1.0.0"
                            />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Build Number</label>
                            <input
                                type="text"
                                name="buildNumber"
                                value={formData.buildNumber}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="e.g. 123"
                            />
                        </div>
                    </div>

                    {app.platform === 'android' && (
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Launcher Activity (Android Only)</label>
                            <input
                                type="text"
                                name="launcherActivity"
                                value={formData.launcherActivity}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="e.g. .MainActivity"
                            />
                        </div>
                    )}

                    <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                        <button type="submit" disabled={isSaving} className="btn btn-primary">
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AppDetail;

